import type { AppointmentFormValues } from '@/types/appointment-form';
import type { DayListRow } from '@/scheduling/lib/calendar-mapping';
import type { OrgLocation } from '@/auth/auth-api';

/**
 * The `CreateAppointmentPanel` pre-fill for the calendar row action "Book follow-up" (PROD-3307 C7). A port of PM
 * `src/scheduling/lib/build-follow-up-from-row.ts` (origin/main 5a49b124), itself Flutter `calendar_tab.dart:2306-2307`
 * (`bookFollowUpAppointment` → `onSelect(appointment, context, false, true)`).
 *
 * The row twin of `buildFollowUpInitialFromAppointment` (the popup's follow-up): same minimal seed, so both entry
 * points behave the same. `referenceAppointmentId = row.id` is the FOLLOWUP-LOCK signal: the profile picker offers
 * only FOLLOWUP-type profiles while it is set (`appointment-profile-section.tsx`). Do not rename or drop it.
 *
 * `firstLocation` is the user's first organisation location when it carries a real id, else null (Flutter
 * `calendar_screen.dart:180`); a truthy-but-empty id is refused here too, so the location picker's own auto-pick runs
 * instead of seeding an empty GUID that 400s on Save. PM logs that refusal with `console.warn`; PA drops it silently.
 */
export function buildFollowUpInitialFromRow(
  row: DayListRow,
  firstLocation: OrgLocation | null,
  selectedDateISO: string,
): Partial<AppointmentFormValues> {
  const locationHasRealId =
    !!firstLocation && typeof firstLocation.id === 'string' && firstLocation.id.length > 0;
  return {
    location: locationHasRealId ? { id: firstLocation!.id, name: firstLocation!.name ?? '' } : null,
    providerId: row.practitionerId ?? null,
    providerName: row.doctorName,
    providerSpeciality: row.doctorSpecialty ?? null,
    patientId: row.patientId ?? null,
    patientName: row.patientName,
    patientPhoto: row.patientAvatar ?? null,
    patientDob: row.dob ?? null,
    // The source reason for visit seeds the new one; a profile that bundles its own overwrites it (Flutter
    // `calendar_screen.dart:170`). The row carries the display only, so code / definition stay blank.
    reasonCode: row.reasonForVisit ? { code: '', display: row.reasonForVisit, definition: '' } : null,
    // FOLLOWUP-LOCK CONTRACT (see above).
    referenceAppointmentId: row.id,
    selectedDate: selectedDateISO,
  } as Partial<AppointmentFormValues>;
}
